import { useQuery } from '@tanstack/react-query';
import type { ScopedPortalItem } from './arcGisPortal';
import { useEnvironmentVariables } from 'utils/useEnvironmentVariables';

export const DEFAULT_WEATHER_LAYERS_IDS = [
  'a6134ae01aad44c499d12feec782b386', // USA Weather Watches and Warnings
  'bba3d0a070d34cea9a303c24a71d5190', // USA Short-Term Weather Warnings
  '248e7b5827a34b248647afb012c58787', // Active Hurricanes, Cyclones and Typhoons
];

const defaultLayerQueryKey = {
  base: ['defaultLayerItems'] as const,
  list: () => [...defaultLayerQueryKey.base] as const,
};

export const useDefaultLayers = () => {
  const { data: envData } = useEnvironmentVariables();
  const itemIdQuery = DEFAULT_WEATHER_LAYERS_IDS.map(
    (itemId) => `id:"${itemId}"`
  ).join(' OR ');

  return useQuery({
    queryKey: defaultLayerQueryKey.list(),
    queryFn: async () => {
      const response = await fetch(
        'https://www.arcgis.com/sharing/rest/search',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            f: 'json',
            q: `(${itemIdQuery})`,
            num: '100',
            token: envData?.ARCGIS_API_KEY ?? '',
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Default layer search failed: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error?.message || 'Error fetching default layers');
      }

      const scopedItems = createApiKeyScopedPortalItems(data.results ?? []);
      const itemById = new Map(
        scopedItems.map((item) => [String(item.id), item])
      );

      return DEFAULT_WEATHER_LAYERS_IDS.map((itemId) =>
        itemById.get(itemId)
      ).filter((item): item is ScopedPortalItem => Boolean(item));
    },
    enabled: !!envData?.ARCGIS_API_KEY,
  });
};

function createApiKeyScopedPortalItems(
  items: __esri.PortalItem[]
): ScopedPortalItem[] {
  const scopedItems = Array.from(items);
  for (const item of scopedItems) {
    (item as ScopedPortalItem).authType = 'apiKey';
    (item as ScopedPortalItem).userScoped = false;
  }

  return scopedItems;
}
